import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

export type CameraMode = "thirdPerson" | "firstPerson";
export type MirrorMode = "off" | "small" | "medium" | "large";

// ── Rear-view mirror constants ─────────────────────────────────────────────
/** Render-target resolution for the mirror (wide, low-res). */
const MIRROR_RT_W    = 512;
const MIRROR_RT_H    = 128;
const MIRROR_ASPECT  = MIRROR_RT_W / MIRROR_RT_H;   // 4.0
/** Base display size in screen pixels (small mode). */
const MIRROR_DISP_H  = 72;
const MIRROR_DISP_W  = MIRROR_DISP_H * MIRROR_ASPECT; // 288
/** Gap between mirror panel and the top edge of the canvas (px). */
const MIRROR_MARGIN  = 8;
/** Scale multipliers for each enabled mirror mode. */
const MIRROR_SCALE: Record<Exclude<MirrorMode, "off">, number> = {
  small:  1.0,
  medium: 1.5,
  large:  2.0,
};

// ── Third-person camera constants ──────────────────────────────────────────
/** Base distance behind the vehicle at rest (metres) */
const TP_BACK_BASE = 8;
/** Extra back distance per m/s of speed */
const TP_BACK_SPEED_SCALE = 0.18;
/** Maximum extra back distance contributed by speed (metres) */
const TP_BACK_MAX_EXTRA = 6;
/** Base height above the vehicle at rest (metres) */
const TP_HEIGHT_BASE = 4;
/** Extra height per m/s of speed */
const TP_HEIGHT_SPEED_SCALE = 0.05;
/** Maximum extra height contributed by speed (metres) */
const TP_HEIGHT_MAX_EXTRA = 2;
/** Height offset for the look-at point above vehicle centre (metres) */
const TP_LOOK_UP = 1;
/** Extra look-at height in rear-view (look-behind) mode — lifts aim point to show more car, less floor */
const TP_LOOK_UP_REAR_EXTRA = 1.2;
/** Exponential-lerp base: smaller = smoother but laggier (1 - base^dt) */
const TP_LERP_BASE = 0.001;
/** Vertical field of view for third-person (degrees) */
const TP_FOV = 62;

// ── Free-look / look-behind camera constants ───────────────────────────────
/** Orbit speed: radians per second at full stick deflection */
const FL_ROTATE_SPEED  = 5.0;
/** Recentre decay constant (1/s) — higher = snappier return to behind */
const FL_RECENTER_SPEED = 8.0;
/** Pitch orbit range (radians) — ±20° */
const FL_PITCH_MAX = 0.35;
/** Pitch speed (radians per second at full stick deflection) */
const FL_PITCH_SPEED = 2.0;

// ── First-person camera constants ──────────────────────────────────────────
/** Vertical field of view for first-person — wider for immersion (degrees) */
const FP_FOV = 75;
/**
 * Rotation-smoothing spring constant (radians per second response rate).
 * Higher = tighter tracking. At 60 fps, alpha ≈ min(1, k / 60) per frame.
 * 35 gives ~58% tracking per frame — fast enough to feel glued, smooth
 * enough to damp micro-corrections from physics snapping.
 */
const FP_ROT_SPRING = 35;
/**
 * Slight downward pitch of the look-at point so the bonnet is barely
 * visible at the bottom of the screen (radians, ~2.3°).
 */
const FP_LOOK_DOWN = 0.04;

export class SceneManager {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  private composer: EffectComposer;
  private bloomPass: UnrealBloomPass;
  private shake = 0;
  private shakeDecay = 6;

  // ── Rear-view mirror ────────────────────────────────────────────────────
  private _mirrorMode: MirrorMode = "small";
  private mirrorCamera!: THREE.PerspectiveCamera;
  private mirrorTarget!: THREE.WebGLRenderTarget;
  private mirrorScene2D!: THREE.Scene;
  private mirrorCam2D!: THREE.OrthographicCamera;
  private mirrorPanel!: THREE.Mesh;
  private mirrorBorder!: THREE.Line;

  /** Current camera mode — drives which follow method is used */
  private mode: CameraMode = "thirdPerson";

  // Scratch Vector3s — reused every frame in followTarget to avoid per-frame heap allocation.
  private readonly _back    = new THREE.Vector3();
  private readonly _desired = new THREE.Vector3();
  private readonly _lookAt  = new THREE.Vector3();
  /**
   * Smoothed rotY for first-person.  Lerped each frame so that physics
   * teleport corrections (rare server reconciliations) don't snap the camera.
   */
  private fpSmoothRotY = 0;

  /** Free-look orbit angle (radians). 0 = camera behind, +π = camera in front. */
  private freeLookYaw = 0;
  /** Free-look pitch offset (radians). Positive = camera higher / looking down. */
  private freeLookPitch = 0;
  /** Tracks previous lookBehind state so transitions can be detected for instant snap. */
  private lookBehindActive = false;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(1.5, window.devicePixelRatio));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.setClearColor(0x04060d, 1);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    this.camera = new THREE.PerspectiveCamera(
      TP_FOV,
      window.innerWidth / window.innerHeight,
      0.1,
      2000,
    );
    this.camera.position.set(0, 8, -16);
    this.camera.lookAt(0, 0, 0);

    this.scene.fog = new THREE.Fog(0x04060d, 60, 220);

    const hemi = new THREE.HemisphereLight(0x88ccff, 0x110022, 0.5);
    this.scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 0.4);
    dir.position.set(40, 80, 30);
    this.scene.add(dir);

    const bloomRes = new THREE.Vector2(
      Math.round(window.innerWidth * 0.5),
      Math.round(window.innerHeight * 0.5),
    );
    this.bloomPass = new UnrealBloomPass(bloomRes, 0.9, 0.4, 0.15);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.composer.addPass(this.bloomPass);

    window.addEventListener("resize", this.onResize);
    this.buildMirror(window.innerWidth, window.innerHeight);
  }

  /** Switch camera mode and adjust FOV.  Snaps fpSmoothRotY to avoid spin. */
  setCameraMode(mode: CameraMode, currentRotY = 0): void {
    this.mode = mode;
    this.freeLookYaw      = 0;
    this.freeLookPitch    = 0;
    this.lookBehindActive = false;
    if (mode === "firstPerson") {
      this.camera.fov = FP_FOV;
      this.fpSmoothRotY = currentRotY; // initialise so there's no spin-up
    } else {
      this.camera.fov = TP_FOV;
    }
    this.camera.updateProjectionMatrix();
  }

  /**
   * Integrate right-stick camera orbit.  Call once per frame before followTarget.
   * stickX/Y are deadzone-filtered values in [-1, 1].
   */
  updateFreeLook(stickX: number, stickY: number, dt: number): void {
    if (Math.abs(stickX) > 0.01) {
      this.freeLookYaw -= stickX * FL_ROTATE_SPEED * dt;
      // Clamp to ±π — full 180° coverage to each side without wrapping
      this.freeLookYaw = Math.max(-Math.PI * 0.98, Math.min(Math.PI * 0.98, this.freeLookYaw));
    } else {
      // Exponential recentre — snaps back quickly when stick released
      this.freeLookYaw *= Math.exp(-FL_RECENTER_SPEED * dt);
    }

    if (Math.abs(stickY) > 0.01) {
      // stickY positive = stick pushed down → camera pitches down (lower angle)
      this.freeLookPitch += stickY * FL_PITCH_SPEED * dt;
      this.freeLookPitch = Math.max(-FL_PITCH_MAX, Math.min(FL_PITCH_MAX, this.freeLookPitch));
    } else {
      this.freeLookPitch *= Math.exp(-FL_RECENTER_SPEED * dt);
    }
  }

  getCameraMode(): CameraMode {
    return this.mode;
  }

  updateDroneCamera(pos: THREE.Vector3, yaw: number, pitch: number): void {
    this.camera.position.copy(pos);
    const sinY = Math.sin(yaw);
    const cosY = Math.cos(yaw);
    const cosP = Math.cos(pitch);
    const sinP = Math.sin(pitch);
    this.camera.lookAt(new THREE.Vector3(
      pos.x + sinY * cosP,
      pos.y + sinP,
      pos.z + cosY * cosP,
    ));
    if (this.camera.fov !== TP_FOV) {
      this.camera.fov = TP_FOV;
      this.camera.updateProjectionMatrix();
    }
  }

  private onResize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    this.composer.setSize(w, h);
    this.bloomPass.resolution.set(Math.round(w * 0.5), Math.round(h * 0.5));
    // Mirror 2D overlay — ortho camera and panel position scale with canvas size
    this.mirrorCam2D.left   = -w / 2;
    this.mirrorCam2D.right  =  w / 2;
    this.mirrorCam2D.top    =  h / 2;
    this.mirrorCam2D.bottom = -h / 2;
    this.mirrorCam2D.updateProjectionMatrix();
    this.applyMirrorLayout(h);
  };

  /**
   * Third-person follow — camera trails behind and above the vehicle.
   * Velocity is used to dynamically extend distance/height at speed.
   *
   * @param lookBehind  When true (keyboard C held), camera moves to the front
   *                    of the car so the player can see what is following them.
   *                    Free-look yaw from the right stick still applies on top.
   */
  followTarget(
    pos: THREE.Vector3,
    rotY: number,
    velocity: THREE.Vector3,
    dt: number,
    lookBehind = false,
  ): void {
    // Detect look-behind state change — resets free-look so rear view is always level.
    const transitioning = lookBehind !== this.lookBehindActive;
    this.lookBehindActive = lookBehind;
    if (transitioning) {
      this.freeLookYaw   = 0;
      this.freeLookPitch = 0;
    }

    const speed = velocity.length();
    const distance = TP_BACK_BASE + Math.min(TP_BACK_MAX_EXTRA, speed * TP_BACK_SPEED_SCALE);

    // Pitch adjusts height: stick down = camera lower; stick up = camera higher
    const pitchHeightDelta = -this.freeLookPitch * 3;
    const height = TP_HEIGHT_BASE + Math.min(TP_HEIGHT_MAX_EXTRA, speed * TP_HEIGHT_SPEED_SCALE) + pitchHeightDelta;

    // Combine look-behind flip with free-look orbit from right stick
    const baseAngle = lookBehind ? rotY + Math.PI : rotY;
    const cameraAngle = baseAngle + this.freeLookYaw;

    this._back.set(-Math.sin(cameraAngle), 0, -Math.cos(cameraAngle));
    this._desired.copy(pos).addScaledVector(this._back, distance);
    this._desired.y = pos.y + Math.max(0.5, height);

    // On transition: snap instantly — no pan. Within a sustained state: normal lerp.
    if (transitioning) {
      this.camera.position.copy(this._desired);
    } else {
      const lerp = 1 - Math.pow(TP_LERP_BASE, dt);
      this.camera.position.lerp(this._desired, lerp);
    }

    // Look-at shifts slightly with pitch: push stick up → look higher
    this._lookAt.copy(pos);
    this._lookAt.y += TP_LOOK_UP + (lookBehind ? TP_LOOK_UP_REAR_EXTRA : 0) + this.freeLookPitch * 2;
    this.camera.lookAt(this._lookAt);

    this.applyShake(dt, 1.0);
  }

  /**
   * First-person follow — places the camera at bonnet/cockpit level.
   *
   * @param pos            Vehicle position at ground level (y ≈ 0)
   * @param rotY           Vehicle heading (radians)
   * @param eyeAboveGround Eye height above ground derived from blueprint dimensions
   * @param eyeForward     Forward offset from vehicle centre along heading (metres)
   * @param dt             Delta-time (seconds)
   */
  followTargetFirstPerson(
    pos: THREE.Vector3,
    rotY: number,
    eyeAboveGround: number,
    eyeForward: number,
    dt: number,
  ): void {
    // Smooth rotY via shortest-arc lerp — damps micro-jitter without adding lag
    const diff = shortestAngDiff(rotY, this.fpSmoothRotY);
    this.fpSmoothRotY += diff * Math.min(1, FP_ROT_SPRING * dt);

    const sinR = Math.sin(this.fpSmoothRotY);
    const cosR = Math.cos(this.fpSmoothRotY);

    // Eye position: forward of vehicle centre at cockpit height
    const eyeX = pos.x + sinR * eyeForward;
    const eyeY = pos.y + eyeAboveGround;
    const eyeZ = pos.z + cosR * eyeForward;

    // Local physics is smooth so position can be set directly (no lerp)
    this.camera.position.set(eyeX, eyeY, eyeZ);

    // Look far along heading with a slight downward tilt
    const lookX = eyeX + sinR * 100;
    const lookY = eyeY - Math.tan(FP_LOOK_DOWN) * 100;
    const lookZ = eyeZ + cosR * 100;
    this.camera.lookAt(lookX, lookY, lookZ);

    // Dampen shake in first-person to avoid nausea
    this.applyShake(dt, 0.25);
  }

  bumpShake(amount: number): void {
    this.shake = Math.min(1.2, this.shake + amount);
  }

  private applyShake(dt: number, scale: number): void {
    if (this.shake <= 0.001) return;
    const amp = this.shake * scale;
    this.camera.position.x += (Math.random() - 0.5) * amp;
    this.camera.position.y += (Math.random() - 0.5) * amp * 0.4;
    this.camera.position.z += (Math.random() - 0.5) * amp;
    this.shake = Math.max(0, this.shake - this.shakeDecay * dt);
  }

  // ── Mirror helpers ─────────────────────────────────────────────────────────

  /** Returns the display {w, h} for the current mirror mode (in pixels). */
  private currentPanelSize(): { w: number; h: number } {
    if (this._mirrorMode === "off") return { w: MIRROR_DISP_W, h: MIRROR_DISP_H };
    const s = MIRROR_SCALE[this._mirrorMode];
    return { w: MIRROR_DISP_W * s, h: MIRROR_DISP_H * s };
  }

  /** Repositions the panel and border to sit at the top-centre for the current mode. */
  private applyMirrorLayout(h: number): void {
    const { h: ph } = this.currentPanelSize();
    const panelY = h / 2 - MIRROR_MARGIN - ph / 2;
    this.mirrorPanel.position.y  = panelY;
    this.mirrorBorder.position.y = panelY;
    const s = this._mirrorMode === "off" ? 0 : MIRROR_SCALE[this._mirrorMode];
    this.mirrorPanel.scale.set(s, s, 1);
    this.mirrorBorder.scale.set(s, s, 1);
    const visible = this._mirrorMode !== "off";
    this.mirrorPanel.visible  = visible;
    this.mirrorBorder.visible = visible;
  }

  private buildMirror(w: number, h: number): void {
    // Rear-facing perspective camera — wide FOV captures more of the scene behind.
    this.mirrorCamera = new THREE.PerspectiveCamera(55, MIRROR_ASPECT, 0.5, 500);

    // Render target at reduced resolution — no bloom, plain render.
    this.mirrorTarget = new THREE.WebGLRenderTarget(MIRROR_RT_W, MIRROR_RT_H);
    // Horizontal flip so left/right match standard mirror convention.
    this.mirrorTarget.texture.repeat.set(-1, 1);
    this.mirrorTarget.texture.offset.set(1, 0);

    // 2D overlay scene with ortho camera in pixel space.
    this.mirrorScene2D = new THREE.Scene();
    this.mirrorCam2D = new THREE.OrthographicCamera(-w / 2, w / 2, h / 2, -h / 2, 0, 10);

    // Panel built at base (small) size — scale is applied per-mode via applyMirrorLayout.
    const panelGeo = new THREE.PlaneGeometry(MIRROR_DISP_W, MIRROR_DISP_H);
    const panelMat = new THREE.MeshBasicMaterial({
      map: this.mirrorTarget.texture,
      depthTest: false,
      depthWrite: false,
    });
    this.mirrorPanel = new THREE.Mesh(panelGeo, panelMat);
    this.mirrorPanel.renderOrder = 1;
    this.mirrorScene2D.add(this.mirrorPanel);

    // Cyan border as a closed line loop around the panel (base size, scaled by applyMirrorLayout).
    const hw = MIRROR_DISP_W / 2;
    const hh = MIRROR_DISP_H / 2;
    const borderPts = [
      new THREE.Vector3(-hw, -hh, 0),
      new THREE.Vector3( hw, -hh, 0),
      new THREE.Vector3( hw,  hh, 0),
      new THREE.Vector3(-hw,  hh, 0),
      new THREE.Vector3(-hw, -hh, 0),
    ];
    const borderGeo = new THREE.BufferGeometry().setFromPoints(borderPts);
    const borderMat = new THREE.LineBasicMaterial({ color: 0x22d3ee, depthTest: false });
    this.mirrorBorder = new THREE.Line(borderGeo, borderMat);
    this.mirrorBorder.position.z = 0.01;
    this.mirrorBorder.renderOrder = 2;
    this.mirrorScene2D.add(this.mirrorBorder);

    // Apply initial layout for the default mode.
    this.applyMirrorLayout(h);
  }

  /**
   * Position the rear-facing mirror camera above the player, looking backward.
   * Call once per frame after the main camera follow update.
   */
  updateMirrorCamera(x: number, y: number, z: number, rotY: number): void {
    const sinR = Math.sin(rotY);
    const cosR = Math.cos(rotY);
    // Slightly behind and above the vehicle looking backward.
    this.mirrorCamera.position.set(x - sinR * 1.5, y + 2.0, z - cosR * 1.5);
    this.mirrorCamera.lookAt(x - sinR * 80, y, z - cosR * 80);
  }

  // ── Mirror mode API ────────────────────────────────────────────────────────

  /** Returns the current mirror mode. */
  getMirrorMode(): MirrorMode {
    return this._mirrorMode;
  }

  /** Sets the mirror mode and updates the overlay layout immediately. */
  setMirrorMode(mode: MirrorMode): void {
    this._mirrorMode = mode;
    this.applyMirrorLayout(window.innerHeight);
  }

  /** Cycles: off → small → medium → large → off. Returns the new mode. */
  cycleMirrorMode(): MirrorMode {
    const ORDER: MirrorMode[] = ["off", "small", "medium", "large"];
    const next = ORDER[(ORDER.indexOf(this._mirrorMode) + 1) % ORDER.length];
    this.setMirrorMode(next);
    return next;
  }

  /**
   * Compatibility alias — toggles between off and small.
   * If currently off, switches to small; otherwise switches to off.
   */
  toggleMirror(): void {
    this.setMirrorMode(this._mirrorMode === "off" ? "small" : "off");
  }

  /**
   * Backward-compat read-only property.  True when mirror mode is not "off".
   * @deprecated Use getMirrorMode() for new code.
   */
  get mirrorEnabled(): boolean {
    return this._mirrorMode !== "off";
  }

  render(): void {
    if (this._mirrorMode !== "off") {
      // Render the scene from the rear camera into the mirror texture.
      this.renderer.setRenderTarget(this.mirrorTarget);
      this.renderer.render(this.scene, this.mirrorCamera);
      this.renderer.setRenderTarget(null);
    }

    // Main view with bloom via EffectComposer.
    this.composer.render();

    if (this._mirrorMode !== "off") {
      // Overlay the mirror panel without clearing the main frame.
      const prevAutoClear = this.renderer.autoClear;
      this.renderer.autoClear = false;
      this.renderer.render(this.mirrorScene2D, this.mirrorCam2D);
      this.renderer.autoClear = prevAutoClear;
    }
  }
}

/** Returns the shortest angular difference from current to target (radians). */
function shortestAngDiff(target: number, current: number): number {
  let d = (target - current) % (Math.PI * 2);
  if (d > Math.PI)  d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
